import type { ReactNode } from 'react';
import { t } from '@/messages/zh-TW';

/**
 * 第二層版型：全站外框（側邊選單 + 頂列 + 麵包屑 + 內容區）。
 *
 * 原系統對應：`layout/decorator.jsp` 的 `#wrapper` → `.navbar-static-side` + `#page-wrapper`；
 * 側邊選單 `layout/menu_general.jsp`、頂列 `layout/navbar.jsp`、麵包屑 `layout/breadcrumb.jsp`。
 *
 * 【證據等級】§7 第 3 級（原始碼）。下列尺寸取自 CSS 檔字面值，未在瀏覽器讀過 computed style，
 * 全部標記【雛形值待實機校準】：
 *   - 側邊欄寬 300px：`css/style.css:388-393`
 *   - 收合後寬 70px：`css/style.css:346-348`（`body.mini-navbar`）
 *   - 內容區左邊距 300px（≥768px）：`css/style.css:3390-3393`
 *   - 內容區內距 20px 10px 40px：`css/style.css:3382-3384`（`.wrapper-content`）
 */
export interface ApplicationShellProps {
  uiId: string;
  /** 頂列，通常放 <Navbar>。 */
  navbar: ReactNode;
  /** 側邊選單，通常放 <SidebarMenu>。 */
  sidebar: ReactNode;
  /** 麵包屑，通常放 <Breadcrumb>；不是每頁都有。 */
  breadcrumb?: ReactNode;
  /** 側邊欄收合（原系統 `body.mini-navbar`）。 */
  collapsed?: boolean;
  children: ReactNode;
}

// ponytail: variant class 用靜態對照表，不動態組字串（§8.1 明文要求，Tailwind 掃不到動態 class）。
const SIDEBAR_WIDTH = {
  expanded: 'md:w-[300px]',
  collapsed: 'md:w-[70px]',
} as const;
const CONTENT_OFFSET = {
  expanded: 'md:ml-[300px]',
  collapsed: 'md:ml-[70px]',
} as const;

export function ApplicationShell({
  uiId,
  navbar,
  sidebar,
  breadcrumb,
  collapsed = false,
  children,
}: ApplicationShellProps) {
  const key = collapsed ? 'collapsed' : 'expanded';
  return (
    <div
      data-ui-id={uiId}
      data-collapsed={collapsed ? 'true' : 'false'}
      className="min-h-screen bg-surface-subtle font-sans text-ink"
    >
      <div
        data-ui-id={`${uiId}-sidebar`}
        className={`fixed inset-y-0 left-0 z-30 hidden overflow-y-auto bg-shell md:block ${SIDEBAR_WIDTH[key]}`}
      >
        {sidebar}
      </div>
      <div data-ui-id={`${uiId}-page`} className={`min-h-screen ${CONTENT_OFFSET[key]}`}>
        {navbar}
        {breadcrumb}
        <main
          data-ui-id={`${uiId}-content`}
          aria-label={t('a11y.mainContent')}
          className="px-[10px] pt-5 pb-10"
        >
          {children}
        </main>
      </div>
    </div>
  );
}
